import {
  CompRef,
  CompStructure,
  DocumentServicesObject,
  PopulatedComponentContainerInfo,
  PopulatedComponentContainerInfoMap,
} from '@wix/document-services-types';
import { BuilderSlotPointer } from '../slotPointer';
import { SPX_REFERENCE_WIDTH } from '../constants';

export const INTERNAL_CONTAINER_COMPONENT_TYPE =
  'wixSystemElements.InternalContainer';

const zeroSpx = () => ({ type: 'spx' as const, value: 0 });

export const ZERO_SPX_BOX = {
  top: zeroSpx(),
  left: zeroSpx(),
  right: zeroSpx(),
  bottom: zeroSpx(),
};

const internalContainerDefinition: CompStructure = {
  componentType: INTERNAL_CONTAINER_COMPONENT_TYPE,
  layouts: {
    type: 'SingleLayoutData',
    componentLayout: {
      type: 'ComponentLayout',
      width: {
        type: 'maxContent',
      },
      height: {
        type: 'auto',
      },
      minHeight: {
        type: 'px',
        value: 0,
      },
    },
    containerLayout: {
      type: 'StackContainerLayout',
      direction: 'row',
      padding: ZERO_SPX_BOX,
    },
    spx: {
      refWidth: SPX_REFERENCE_WIDTH,
      resolverType: 'scale',
    },
  },
};

const getPopulatedSlotInfo = (
  infoMap: PopulatedComponentContainerInfoMap,
  slotKeyName: string,
): PopulatedComponentContainerInfo | undefined => {
  return (
    infoMap[slotKeyName] ??
    Object.values(infoMap).find((info) => info?.slotName === slotKeyName)
  );
};

const getPopulatedSlotContentRef = (
  documentServicesAPI: DocumentServicesObject,
  path: BuilderSlotPointer,
): CompRef | undefined => {
  const infoMap =
    typeof path.innerElementPath === 'undefined'
      ? documentServicesAPI.components.slots.builder.getPopulatedComponentContainerInfoMap(
          path.hostRef,
        )
      : documentServicesAPI.components.innerElements.slots.getPopulatedComponentContainerInfoMap(
          path.hostRef,
          path.innerElementPath,
        );

  return getPopulatedSlotInfo(infoMap, path.slotKeyName)?.compRef;
};

const populateInternalContainer = (
  documentServicesAPI: DocumentServicesObject,
  path: BuilderSlotPointer,
): CompRef => {
  if (typeof path.innerElementPath === 'undefined') {
    return documentServicesAPI.components.slots.populate(
      path.hostRef,
      path.slotKeyName,
      internalContainerDefinition,
    );
  }

  return documentServicesAPI.components.innerElements.slots.populate(
    path.hostRef,
    path.innerElementPath,
    path.slotKeyName,
    internalContainerDefinition,
  ) as CompRef;
};

export const getOrCreateInternalContainer = (
  documentServicesAPI: DocumentServicesObject,
  path: BuilderSlotPointer,
): CompRef => {
  const populatedSlotContentRef = getPopulatedSlotContentRef(
    documentServicesAPI,
    path,
  );

  if (!populatedSlotContentRef) {
    return populateInternalContainer(documentServicesAPI, path);
  }

  const componentType = documentServicesAPI.components.getType(
    populatedSlotContentRef,
  );

  if (componentType !== INTERNAL_CONTAINER_COMPONENT_TYPE) {
    throw new Error(
      `Header plugin slot "${path.slotKeyName}" is already populated with "${componentType}". Expected "${INTERNAL_CONTAINER_COMPONENT_TYPE}".`,
    );
  }

  return populatedSlotContentRef;
};
